import React, { useRef, useEffect, useMemo } from 'react';
import AIMessageBubble from './AIMessageBubble';
import { MagicBallIcon } from '../../../design-system/icons';
import AIProcessingIndicator from './AIProcessingIndicator';
import { surfaces, textColors, typography } from '../../../design-system/tokens';

const AIChatMessages = ({ chat, extraMessages = [], onPrefillDraft = () => {}, onFocusComposer = () => {}, isReviewMode = false }) => {
  const messagesEndRef = useRef(null);
  const baseMessages = chat?.messages || [];
  const messages = useMemo(() => {
    const extras = Array.isArray(extraMessages) ? extraMessages : [];
    // Keep order stable; if timestamps are present, sort by them.
    const merged = [...baseMessages, ...extras];
    return merged.sort((a, b) => {
      const ta = new Date(a?.timestamp || a?.created_at || 0).getTime();
      const tb = new Date(b?.timestamp || b?.created_at || 0).getTime();
      return ta - tb;
    });
  }, [baseMessages, extraMessages]);
  const latestMessageId = messages.length > 0 ? messages[messages.length - 1]?.id : null;
  const chatId = chat?.id;
  
  // Check if any operations are currently executing
  const hasExecutingOperations = useMemo(() => {
    return messages.some(msg => 
      msg.operations?.some(op => 
        op.execution_status === 'pending' || op.execution_status === 'executing'
      )
    );
  }, [messages]);

  // Show the processing indicator immediately after sending (optimistic user message),
  // and keep showing it while operations are executing.
  const hasPendingUserMessage = useMemo(() => {
    return messages.some((msg) => msg.role === 'user' && !!msg.pending);
  }, [messages]);

  const shouldShowProcessingIndicator = messages.length > 0 && (hasPendingUserMessage || hasExecutingOperations);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  return (
    <div className={`flex-1 min-h-0 overflow-y-auto ${surfaces.secondary}`}>
      <div className="relative min-h-full flex flex-col">
        {isReviewMode ? (
          <div
            data-testid="ai-chat-messages-review-overlay"
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-green-400/20"
          />
        ) : null}
        <div className="relative z-10 p-4 space-y-4 flex-1">
          {messages.length === 0 ? (
          <div className={`text-center text-sm mt-8 ${textColors.secondary}`}>
            <div className="flex justify-center mb-3">
              <MagicBallIcon pixelSize={72} />
            </div>
            <p className={`font-medium ${textColors.primary}`}>Hi! I'm your AI design assistant.</p>
            <p className="mt-2">Ask me to modify your website!</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <AIMessageBubble
                key={message.id}
                message={message}
                chatId={chatId}
                isLatestMessage={String(message?.id) === String(latestMessageId)}
                onPrefillDraft={onPrefillDraft}
                onFocusComposer={onFocusComposer}
              />
            ))}
            
            {/* AI processing indicator (shown while operations run) */}
            {shouldShowProcessingIndicator && (
              <AIProcessingIndicator className="py-2" />
            )}
          </>
        )}
        <div ref={messagesEndRef} />
        </div>
      </div>
    </div>
  );
};

export default AIChatMessages;
